import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PhotoUpload } from "@/components/PhotoUpload";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const sel = "w-full h-11 rounded-xl border border-[#E5DEC9] bg-white px-3 text-sm";
const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-[#52635B]">{t(label)}</Label>{children}</div>;

export const ProfileEditor = ({ p, onSaved, testid = "profile-editor" }) => {
  const [f, setF] = useState({ ...p });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const toInt = (v) => (v ? parseInt(v, 10) : null);
    try {
      await api.put(`/persons/${p.person_id}`, {
        name: f.name, gender: f.gender || null, bio: f.bio || "", photo_url: f.photo_url || "",
        birth_year: toInt(f.birth_year), death_year: toInt(f.death_year),
        birth_date: f.birth_date || null, wedding_date: f.wedding_date || null,
        education: f.education || "", occupation: f.occupation || "",
      });
      toast.success("Profile saved");
      onSaved?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid={testid}>
      <PhotoUpload value={f.photo_url} name={f.name} onChange={(url) => setF({ ...f, photo_url: url })} testid={`${testid}-photo`} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name"><Input required value={f.name} onChange={set("name")} className="h-11 rounded-xl" data-testid={`${testid}-name`} /></Field>
        <Field label="Gender">
          <select value={f.gender || ""} onChange={set("gender")} className={sel} data-testid={`${testid}-gender`}>
            <option value="">{t("Prefer not to say")}</option><option value="male">{t("Male")}</option><option value="female">{t("Female")}</option><option value="other">{t("Other")}</option>
          </select>
        </Field>
        <Field label="Birth date"><Input type="date" value={f.birth_date || ""} onChange={set("birth_date")} className="h-11 rounded-xl" data-testid={`${testid}-birth-date`} /></Field>
        <Field label="Birth year (if date unknown)"><Input type="number" value={f.birth_year || ""} onChange={set("birth_year")} disabled={!!f.birth_date} className="h-11 rounded-xl" data-testid={`${testid}-birth-year`} /></Field>
        <Field label="Education"><Input value={f.education || ""} onChange={set("education")} placeholder="e.g. B.Tech, IIT Delhi" className="h-11 rounded-xl" data-testid={`${testid}-education`} /></Field>
        <Field label="Current occupation"><Input value={f.occupation || ""} onChange={set("occupation")} placeholder="e.g. Farmer, Teacher, Business" className="h-11 rounded-xl" data-testid={`${testid}-occupation`} /></Field>
        <Field label="Wedding date"><Input type="date" value={f.wedding_date || ""} onChange={set("wedding_date")} className="h-11 rounded-xl" data-testid={`${testid}-wedding-date`} /></Field>
        <Field label="Year of passing (if any)"><Input type="number" value={f.death_year || ""} onChange={set("death_year")} className="h-11 rounded-xl" data-testid={`${testid}-death-year`} /></Field>
      </div>
      <Field label="Story"><Textarea value={f.bio || ""} onChange={set("bio")} className="rounded-xl min-h-[100px]" data-testid={`${testid}-bio`} /></Field>
      <Button type="submit" disabled={busy} className="rounded-full bg-[#1E3A2B] hover:bg-[#162C20] h-11 px-8" data-testid={`${testid}-save`}>{busy ? "…" : t("Save profile")}</Button>
    </form>
  );
};
